/**
 * Ask Bar (Phase 36.2, chunk 6)
 *
 * ONE GLOBAL BAR IN THE HEADER — not a Cmd+K palette and not a single page.
 * The header is manager-and-above by construction (`OperatorRedirect` bounces
 * operators out of the whole `DashboardLayout` subtree), and it was near-empty.
 * A palette would need a dependency the repo does not have and a global keydown
 * listener that does not exist — and a hidden shortcut does not TEACH that the
 * feature exists, which is most of the value. Confining it to `/dashboard` was
 * rejected for the obvious reason: making the cure for "I can't find the right
 * tab" reachable from only one tab is self-defeating.
 *
 * The four outcomes each render differently, and three of them are honest
 * refusals rather than answers:
 *   results   — sections with counts and rows
 *   empty     — the question resolved; nothing is there. Says what IS there.
 *   ambiguous — the term matched several rows; the user picks. Never a guess.
 *   no_signal — nothing matched; offers examples built from the org's own data.
 *
 * `caveats` render above the results in every outcome. They are the delivery
 * end of the no-silent-drop rule — a parser that records what it could not use
 * and a UI that hides it are the same product failure.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Info, Scan } from 'lucide-react';
import type { AskResponse } from '@guild/shared';
import { useAsk } from '../../hooks/useAsk';
import { VideoAnalysisModal } from '../../pages/assessments/components/VideoAnalysisModal';

export function AskBar() {
  const navigate = useNavigate();
  const ask = useAsk();
  const [value, setValue] = useState('');
  const [result, setResult] = useState<AskResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAnalyze, setShowAnalyze] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const run = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      if (!trimmed || ask.isPending) return;
      setError(null);
      ask.mutate(
        { question: trimmed },
        {
          onSuccess: (data) => setResult(data),
          onError: (err) =>
            setError(err instanceof Error ? err.message : 'Something went wrong. Tap to retry.'),
        }
      );
    },
    [ask]
  );

  const close = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  // Dismiss the panel on an outside click or Escape — it is an overlay in a
  // shared header, so it must not trap the rest of the app.
  useEffect(() => {
    if (!result && !error) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [result, error, close]);

  const open = result !== null || error !== null;

  return (
    <div ref={containerRef} className="relative w-full max-w-xl">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(value);
        }}
      >
        <div className="relative">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Ask about a camera, zone, operator or shift…"
            aria-label="Ask a question about your site"
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-9 text-sm outline-none focus:border-guild-green focus:ring-1 focus:ring-guild-green/30"
          />
          {value && (
            <button
              type="button"
              onClick={() => {
                setValue('');
                close();
              }}
              aria-label="Clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </form>

      {open && (
        // Above the header's own z-40 so the panel is not clipped by it, and
        // below the staging banner's z-[60].
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
          {error !== null ? (
            <div className="m-3 rounded-lg bg-red-50 px-4 py-3 ring-1 ring-red-200">
              <p className="text-sm text-red-700">{error}</p>
              <button
                type="button"
                onClick={() => run(value)}
                className="mt-2 text-sm font-semibold text-red-700 underline"
              >
                Tap to retry
              </button>
            </div>
          ) : (
            result && (
              <AskPanel
                result={result}
                onPick={(term) => {
                  setValue(term);
                  run(term);
                }}
                onNavigate={(href) => {
                  close();
                  navigate(href);
                }}
                onAnalyze={() => setShowAnalyze(true)}
              />
            )
          )}
        </div>
      )}

      {/* CONDITIONAL RENDER, so the modal fully UNMOUNTS between opens.
          `VideoAnalysisModal` early-returns on `!isOpen` rather than
          unmounting, and its state survives that — without this, a pre-fill
          from one question could be stranded on the next. */}
      {showAnalyze && result?.analyze && (
        <VideoAnalysisModal
          isOpen
          onClose={() => setShowAnalyze(false)}
          initialValues={{
            // The camera CONFIG uuid — the modal's <select> holds this and
            // remaps to the Alta GUID itself. Never pass the GUID.
            cameraId: result.analyze.cameraConfigId,
            sourceNumber: result.analyze.sourceNumber,
            startTime: result.analyze.startTime,
            endTime: result.analyze.endTime,
          }}
        />
      )}

      {ask.isPending && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500 shadow-lg">
          Looking…
        </div>
      )}
    </div>
  );
}

function AskPanel({
  result,
  onPick,
  onNavigate,
  onAnalyze,
}: {
  result: AskResponse;
  onPick: (term: string) => void;
  onNavigate: (href: string) => void;
  onAnalyze: () => void;
}) {
  return (
    <div className="divide-y divide-gray-100">
      <div className="px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          {result.interpretation}
        </p>
        <p className="mt-1 text-sm text-gray-800">{result.summary}</p>
      </div>

      {result.caveats.length > 0 && (
        <div className="bg-amber-50/60 px-4 py-2">
          {result.caveats.map((caveat) => (
            <p key={caveat} className="flex items-start gap-1.5 text-xs text-amber-800">
              <Info size={13} className="mt-0.5 shrink-0" />
              <span>{caveat}</span>
            </p>
          ))}
        </div>
      )}

      {result.candidates.map((group) => (
        <div key={group.term} className="px-4 py-3">
          <p className="text-xs text-gray-500">Which “{group.term}” did you mean?</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {group.candidates.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                onClick={() => onPick(candidate.label)}
                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:border-guild-green hover:text-guild-green"
              >
                {candidate.label}
              </button>
            ))}
          </div>
        </div>
      ))}

      {result.sections
        .filter((section) => section.rows.length > 0)
        .map((section) => (
          <div key={section.kind} className="px-2 py-2">
            {/* `10+`, never a bare `10` — `count` is capped at RESULT_LIMIT
                and rendering a cap as an exact count is a fabricated number. */}
            <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
              {section.label} ({section.count}
              {section.truncated ? '+' : ''})
            </p>
            {section.rows.map((row) =>
              row.href ? (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => onNavigate(row.href as string)}
                  className="block w-full rounded-lg px-2 py-1.5 text-left hover:bg-gray-50"
                >
                  <RowBody label={row.label} sublabel={row.sublabel} />
                </button>
              ) : (
                <div key={row.id} className="px-2 py-1.5">
                  <RowBody label={row.label} sublabel={row.sublabel} />
                </div>
              )
            )}
          </div>
        ))}

      {/* A HAND-OFF, never a write. The bar issues no analysis — it opens the
          existing form pre-filled and the user completes SOP + Jobs there. */}
      {result.analyze && (
        <div className="px-4 py-3">
          <button
            type="button"
            onClick={onAnalyze}
            className="inline-flex items-center gap-2 rounded-lg bg-guild-green px-3 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            <Scan size={15} strokeWidth={2.5} />
            {result.analyze.label}
          </button>
          <p className="mt-1.5 text-xs text-gray-500">
            Opens the analysis form pre-filled — you still pick the SOP and Jobs.
          </p>
        </div>
      )}

      {result.examples.length > 0 && (
        <div className="px-4 py-3">
          <p className="text-xs text-gray-500">Try one of these:</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {result.examples.map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => onPick(example)}
                className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:border-guild-green hover:text-guild-green"
              >
                {example}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RowBody({ label, sublabel }: { label: string; sublabel: string | null }) {
  return (
    <>
      <span className="block truncate text-sm font-medium text-gray-800">{label}</span>
      {sublabel && <span className="block truncate text-xs text-gray-500">{sublabel}</span>}
    </>
  );
}
